import * as React from 'react';
import { Trash2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { EndpointMacro } from '../../model';

interface EndpointMacroDialogProps {
  endpoint: string;
  macros: EndpointMacro[];
  autoReplaceDisabled?: boolean | undefined;
  onAutoReplace?: ((endpoint: string) => Promise<string>) | undefined;
  onApply: (endpoint: string) => void;
  onClose: () => void;
}

interface EndpointParameter {
  id: number;
  name: string;
  value: string;
  hasSeparator: boolean;
  locked: boolean;
}

export function EndpointMacroDialog({ endpoint, ...props }: EndpointMacroDialogProps) {
  return <EndpointMacroDialogContent key={endpoint} endpoint={endpoint} {...props} />;
}

function EndpointMacroDialogContent({
  endpoint,
  macros,
  autoReplaceDisabled,
  onAutoReplace,
  onApply,
  onClose,
}: EndpointMacroDialogProps) {
  const parsed = React.useMemo(() => parseEndpoint(endpoint), [endpoint]);
  const [base, setBase] = React.useState(parsed.base);
  const [parameters, setParameters] = React.useState(parsed.parameters);
  const [autoReplacing, setAutoReplacing] = React.useState(false);
  const preview = serializeEndpoint(base, parameters);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-[1100px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle>{dashboardT('endpointGenerator.macros.title')}</DialogTitle>
          <DialogDescription>
            {dashboardT('endpointGenerator.macros.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[70vh] overflow-y-auto lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
          <div className="space-y-3 p-5 lg:border-r">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold">
                {dashboardT('endpointGenerator.macros.detected', {
                  count: String(parameters.filter(({ locked }) => !locked).length),
                })}
              </p>
              {onAutoReplace ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={autoReplaceDisabled || autoReplacing}
                  onClick={() => {
                    setAutoReplacing(true);
                    void onAutoReplace(preview)
                      .then((nextEndpoint) => {
                        const next = parseEndpoint(nextEndpoint);
                        setBase(next.base);
                        setParameters(next.parameters);
                      })
                      .catch(() => undefined)
                      .finally(() => {
                        setAutoReplacing(false);
                      });
                  }}
                >
                  {dashboardT(
                    autoReplacing
                      ? 'endpointGenerator.autoReplacing'
                      : 'endpointGenerator.autoReplace',
                  )}
                </Button>
              ) : null}
            </div>
            {parameters
              .filter(({ locked }) => !locked)
              .map((parameter) => (
                <div
                  key={parameter.id}
                  className="grid min-w-0 gap-2 sm:grid-cols-[minmax(120px,0.45fr)_minmax(0,1fr)_180px_36px] sm:items-end"
                >
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs">{dashboardT('endpointGenerator.parameter')}</Label>
                    <div className="bg-muted/40 truncate rounded-md border px-3 py-2 text-sm">
                      {parameter.name}
                    </div>
                  </div>
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs">{dashboardT('endpointGenerator.value')}</Label>
                    <Input
                      value={parameter.value}
                      onChange={(event) => {
                        const value = event.target.value;
                        setParameters((current) =>
                          current.map((item) =>
                            item.id === parameter.id ? { ...item, value } : item,
                          ),
                        );
                      }}
                    />
                  </div>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      setParameters((current) =>
                        current.map((item) =>
                          item.id === parameter.id ? { ...item, value } : item,
                        ),
                      );
                    }}
                  >
                    <SelectTrigger aria-label={dashboardT('endpointGenerator.macros.choose')}>
                      <SelectValue placeholder={dashboardT('endpointGenerator.macros.choose')} />
                    </SelectTrigger>
                    <SelectContent>
                      {macros.map((macro) => (
                        <SelectItem key={`${macro.name ?? ''}-${macro.value}`} value={macro.value}>
                          {macro.name?.trim() || macro.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={dashboardT('endpointGenerator.macros.remove')}
                    onClick={() => {
                      setParameters((current) =>
                        current.filter((item) => item.id !== parameter.id),
                      );
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            {!parameters.some(({ locked }) => !locked) ? (
              <p className="text-muted-foreground py-8 text-center text-sm">
                {dashboardT('endpointGenerator.macros.empty')}
              </p>
            ) : null}
          </div>
          <div className="min-w-0 space-y-2 p-5">
            <p className="text-sm font-semibold">{dashboardT('endpointGenerator.preview')}</p>
            <pre className="bg-muted/35 max-h-[50vh] rounded-md border p-4 font-mono text-xs break-all whitespace-pre-wrap">
              {preview}
            </pre>
          </div>
        </div>
        <DialogFooter className="border-border border-t px-5 py-3.5">
          <Button type="button" variant="outline" onClick={onClose}>
            {dashboardT('endpointGenerator.cancel')}
          </Button>
          <Button
            type="button"
            disabled={autoReplacing}
            onClick={() => {
              onApply(preview);
            }}
          >
            {dashboardT('endpointGenerator.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseEndpoint(endpoint: string) {
  const queryIndex = endpoint.indexOf('?');
  if (queryIndex < 0) return { base: endpoint, parameters: [] };
  const base = endpoint.slice(0, queryIndex);
  const parameters = endpoint
    .slice(queryIndex + 1)
    .split('&')
    .filter(Boolean)
    .map((pair, index) => {
      const separator = pair.indexOf('=');
      const name = decodeEndpointComponent(separator < 0 ? pair : pair.slice(0, separator));
      return {
        id: index,
        name,
        value: separator < 0 ? '' : decodeEndpointComponent(pair.slice(separator + 1)),
        hasSeparator: separator >= 0,
        locked: name === 'pid' || name === 'placementId',
      };
    });
  return { base, parameters };
}

function serializeEndpoint(base: string, parameters: EndpointParameter[]) {
  return parameters.length
    ? `${base}?${parameters
        .map(({ name, value, hasSeparator }) => {
          const encodedName = encodeURIComponent(name);
          return hasSeparator ? `${encodedName}=${encodeURIComponent(value)}` : encodedName;
        })
        .join('&')}`
    : base;
}

function decodeEndpointComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
